/** Phase 3A.1 — AI Assistant page.
 * Goal → POST /ai/campaign-brief → render proposal → materialize.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, auth } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../../components/ui/dialog";
import { toast } from "sonner";
import { showAgentError } from "../../components/app/AgentErrorToast";
import { AgentGenerationSpinner } from "../../components/app/AgentGenerationSpinner";
import AIRunBadge from "../../components/app/AIRunBadge";
import EmptyState from "../../components/app/EmptyState";

const ALLOWED_ROLES = new Set(["owner", "admin", "manager"]);

function qualityClass(q) {
  if (q == null) return "";
  if (q >= 0.75) return "bg-emerald-600 text-white border-transparent";
  if (q >= 0.5)  return "bg-amber-500 text-white border-transparent";
  return "bg-red-600 text-white border-transparent";
}

export default function Assistant() {
  const t = useT();
  const navigate = useNavigate();
  const role = (auth.getUser() || {}).role || "";
  const canUse = ALLOWED_ROLES.has(role);

  const [goal, setGoal] = useState("");
  const [locale, setLocale] = useState("de");
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(0); // 0 idle, 1 analyzing, 2 strategy, 3 copy
  const [proposal, setProposal] = useState(null);
  const [latencyMs, setLatencyMs] = useState(undefined);
  const [runId, setRunId] = useState(null);
  const [safety, setSafety] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [labelMap, setLabelMap] = useState({ regions: {}, target_groups: {}, products: {}, services: {} });
  const [editsOpen, setEditsOpen] = useState(false);
  const [editsText, setEditsText] = useState("{}");
  const [discardOpen, setDiscardOpen] = useState(false);
  const [materializing, setMaterializing] = useState(false);
  const [useByPlatform, setUseByPlatform] = useState({});

  // Resolve brand-profile labels once per session
  useEffect(() => {
    if (!canUse) return;
    api.get("/brand-profile").then((r) => {
      const bp = r.data || {};
      const m = (arr) => Object.fromEntries((arr || []).map((x) => [x.id, x.name || x.label || x.id]));
      setLabelMap({
        regions: m(bp.regions),
        target_groups: m(bp.target_groups || bp.buyer_personas),
        products: m(bp.products), services: m(bp.services),
      });
    }).catch(() => {});
  }, [canUse]);

  // NOTE: this useMemo must be declared before the early-return below to
  // satisfy react-hooks/rules-of-hooks. (Moved here in Phase 3A.1 testing pass.)
  const remapDecision = useMemo(
    () => decisions.find((d) => /remap|default/i.test(d.decision || "")),
    [decisions],
  );

  if (!canUse) {
    return (
      <div data-testid="assistant-rbac-empty">
        <EmptyState
          title={t("assistant.requires_manager_title")}
          description={t("assistant.requires_manager")}
        />
      </div>
    );
  }

  const submit = async () => {
    if (!goal.trim()) { toast.error(t("assistant.goal_required")); return; }
    setProposal(null); setRunId(null); setSafety(null); setDecisions([]); setUseByPlatform({});
    setRunning(true); setStep(1);
    const stepTimers = [
      setTimeout(() => setStep(2), 1200),
      setTimeout(() => setStep(3), 3000),
    ];
    try {
      const r = await api.post("/ai/campaign-brief",
        { goal, locale, explicit_overrides: {} },
        { skipPermissionToast: false });
      const out = r.data.output || {};
      setProposal(out);
      const lat = r.data.latency_ms ?? out.latency_ms;
      setLatencyMs(lat);
      // Phase 3A.1.3 — surface contract drift loudly if a succeeded run
      // returns without latency_ms (badge would otherwise silently render '—').
      if (r.data.status === "succeeded" && (lat === undefined || lat === null)) {
        // eslint-disable-next-line no-console
        console.error(
          "[VIONA contract drift] /ai/campaign-brief succeeded but response has no latency_ms",
          { run_id: r.data.run_id, keys: Object.keys(r.data) },
        );
      }
      setRunId(r.data.run_id);
      setSafety(r.data.safety);
      // default "Use this" to top-scoring variant per platform
      const init = {};
      Object.entries(out.copy_by_platform || {}).forEach(([p, vs]) => {
        const top = (vs || []).reduce((best, v, i) =>
          (v.quality_score || 0) > (best.q || -1) ? { i, q: v.quality_score || 0 } : best,
          { i: 0, q: -1 });
        init[p] = top.i;
      });
      setUseByPlatform(init);
      // fetch decisions (for hallucination remap chip)
      if (r.data.run_id) {
        api.get(`/agents/runs/${r.data.run_id}/decisions`)
          .then((d) => setDecisions(d.data.items || []))
          .catch(() => {});
      }
    } catch (e) {
      showAgentError({ err: e, locale: (t("common.locale_code") || "de") });
      toast.error(t("assistant.error_generating"));
    } finally {
      stepTimers.forEach(clearTimeout);
      setRunning(false); setStep(0);
    }
  };

  const materialize = async () => {
    if (!runId) return;
    setMaterializing(true);
    try {
      let edits = {};
      if (editsText.trim() && editsText.trim() !== "{}") {
        try { edits = JSON.parse(editsText); }
        catch { toast.error("Invalid JSON in edits"); setMaterializing(false); return; }
      }
      const r = await api.post(`/ai/campaign-brief/${runId}/materialize`, { edits });
      toast.success(t("assistant.materialized"));
      navigate(`/app/campaigns/${r.data.campaign_id}`);
    } catch (e) {
      toast.error(t("assistant.materialize_failed"));
    } finally {
      setMaterializing(false);
    }
  };

  const ids2names = (kind, ids) =>
    (ids || []).map((id) => labelMap[kind][id] || id);

  return (
    <div className="space-y-6" data-testid="assistant-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">{t("nav.assistant")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("assistant.subtitle")}</p>
      </div>

      {/* Input card */}
      <Card>
        <CardContent className="pt-6 space-y-3">
          <label className="text-sm font-medium">{t("assistant.goal_label")}</label>
          <Textarea
            data-testid="assistant-goal"
            placeholder={t("assistant.goal_placeholder")}
            value={goal} onChange={(e) => setGoal(e.target.value)}
            rows={4}
            className="font-sans"
          />
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-2">
              <Button variant={locale === "de" ? "default" : "outline"} size="sm"
                      onClick={() => setLocale("de")} data-testid="assistant-locale-de">DE</Button>
              <Button variant={locale === "en" ? "default" : "outline"} size="sm"
                      onClick={() => setLocale("en")} data-testid="assistant-locale-en">EN</Button>
            </div>
            <Button onClick={submit} disabled={running || !goal.trim()}
                    data-testid="assistant-generate" className="ml-auto">
              {running ? t("assistant.generating") : t("assistant.generate")}
            </Button>
          </div>
          {running && (
            <div className="mt-3" data-testid="assistant-generating-spinner-wrap">
              <AgentGenerationSpinner active locale={t("common.locale_code") || "de"} />
            </div>
          )}
          {running && (
            <div className="text-xs text-muted-foreground space-y-1 mt-2" data-testid="assistant-progress">
              <div className={step >= 1 ? "text-foreground font-medium" : ""}>1. {t("assistant.step_analyzing")}</div>
              <div className={step >= 2 ? "text-foreground font-medium" : ""}>2. {t("assistant.step_strategy")}</div>
              <div className={step >= 3 ? "text-foreground font-medium" : ""}>3. {t("assistant.step_copy")}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Proposal render */}
      {proposal && (
        <div className="space-y-4" data-testid="assistant-proposal">
          {/* Header */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-2xl" data-testid="proposal-name">{proposal.name}</CardTitle>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <Badge variant="secondary" data-testid="proposal-type">{proposal.campaign_type}</Badge>
                    <Badge variant="outline">confidence {Number(proposal.confidence || 0).toFixed(2)}</Badge>
                    {proposal.mocked && <Badge variant="destructive">mocked</Badge>}
                  </div>
                </div>
                <AIRunBadge
                  model={proposal.model}
                  cost_usd={proposal.cost_usd}
                  latency_ms={latencyMs}
                  quality_score={proposal.aggregate_quality_score}
                />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm" data-testid="proposal-goal-summary">{proposal.goal_summary}</p>
              {remapDecision && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-1.5 text-xs"
                     data-testid="hallucination-remap-chip">
                  <span className="font-medium">ℹ {t("assistant.hallucination_remap_chip")}</span>
                  <span className="text-muted-foreground truncate max-w-md">
                    {(remapDecision.reasoning || "").slice(0, 140)}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Audience + Budget */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card><CardHeader><CardTitle className="text-base">{t("assistant.audience")}</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-2">
                <div><span className="text-muted-foreground">{t("assistant.regions")}:</span> {ids2names("regions", proposal.selected_region_ids).join(", ") || "—"}</div>
                <div><span className="text-muted-foreground">{t("assistant.personas")}:</span> {ids2names("target_groups", proposal.selected_target_group_ids).join(", ") || "—"}</div>
                <div><span className="text-muted-foreground">{t("assistant.products")}:</span> {ids2names("products", proposal.selected_product_ids).join(", ") || "—"}</div>
                <div><span className="text-muted-foreground">{t("assistant.services")}:</span> {ids2names("services", proposal.selected_service_ids).join(", ") || "—"}</div>
              </CardContent>
            </Card>
            <Card><CardHeader><CardTitle className="text-base">{t("assistant.budget_schedule")}</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-2">
                <div><span className="text-muted-foreground">Budget:</span> {(proposal.budget || {}).amount} {(proposal.budget || {}).currency} / {(proposal.budget || {}).type}</div>
                <div><span className="text-muted-foreground">Pacing:</span> {(proposal.budget || {}).pacing || "—"}</div>
                <div><span className="text-muted-foreground">Platforms:</span> {(proposal.platforms || []).join(", ") || "—"}</div>
                <div><span className="text-muted-foreground">KPI:</span> {proposal.goal_kpi} ({proposal.goal_target_value ?? "—"})</div>
              </CardContent>
            </Card>
          </div>

          {/* Offer + tracking + CRM */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><CardHeader><CardTitle className="text-base">{t("assistant.offer")}</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="font-medium">{(proposal.offer || {}).headline}</div>
                <div className="text-muted-foreground">{(proposal.offer || {}).value_prop}</div>
                <div className="mt-2"><Badge variant="outline">CTA: {(proposal.offer || {}).cta}</Badge></div>
              </CardContent>
            </Card>
            <Card><CardHeader><CardTitle className="text-base">{t("assistant.tracking")}</CardTitle></CardHeader>
              <CardContent className="text-xs font-mono space-y-1">
                <div>utm_source: {(proposal.tracking || {}).utm_source || "—"}</div>
                <div>utm_medium: {(proposal.tracking || {}).utm_medium || "—"}</div>
                <div>utm_campaign: {(proposal.tracking || {}).utm_campaign || "—"}</div>
                <div>event: {(proposal.tracking || {}).conversion_event || "—"}</div>
              </CardContent>
            </Card>
            <Card><CardHeader><CardTitle className="text-base">{t("assistant.crm_goal")}</CardTitle></CardHeader>
              <CardContent className="text-xs font-mono space-y-1">
                <div>connector: {(proposal.crm_goal || {}).connector_key || "—"}</div>
                <div>stage: {(proposal.crm_goal || {}).pipeline_stage || "—"}</div>
                <div>tag: {(proposal.crm_goal || {}).lead_source_tag || "—"}</div>
              </CardContent>
            </Card>
          </div>

          {/* Copy per platform */}
          <Card><CardHeader><CardTitle className="text-base">{t("assistant.ad_copy")}</CardTitle></CardHeader>
            <CardContent>
              {Object.keys(proposal.copy_by_platform || {}).length === 0 ? (
                <div className="text-sm text-muted-foreground">No copy generated.</div>
              ) : (
                <Tabs defaultValue={Object.keys(proposal.copy_by_platform)[0]}>
                  <TabsList className="flex-wrap h-auto">
                    {Object.keys(proposal.copy_by_platform).map((p) => (
                      <TabsTrigger key={p} value={p} data-testid={`copy-platform-tab-${p}`}>{p}</TabsTrigger>
                    ))}
                  </TabsList>
                  {Object.entries(proposal.copy_by_platform).map(([p, vs]) => (
                    <TabsContent key={p} value={p} className="space-y-3 mt-3">
                      {(vs || []).map((v, i) => (
                        <Card key={i} className={i === useByPlatform[p] ? "ring-2 ring-primary" : ""}
                              data-testid={`copy-variant-${p}-${i}`}>
                          <CardContent className="pt-4 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline">{v.framework || "—"}</Badge>
                              <Badge className={`font-mono ${qualityClass(v.quality_score)}`}
                                     data-testid={`copy-q-${p}-${i}`}>
                                Q {Number(v.quality_score || 0).toFixed(2)}
                              </Badge>
                              {(v.char_violations || []).length > 0 && (
                                <Badge variant="destructive" data-testid={`copy-cv-${p}-${i}`}>
                                  {v.char_violations.length} {t("assistant.char_violations")}
                                </Badge>
                              )}
                              <Button size="sm"
                                      variant={i === useByPlatform[p] ? "default" : "outline"}
                                      onClick={() => setUseByPlatform({ ...useByPlatform, [p]: i })}
                                      className="ml-auto"
                                      data-testid={`copy-use-${p}-${i}`}>
                                {i === useByPlatform[p] ? "✓ " : ""}{t("assistant.use_this")}
                              </Button>
                            </div>
                            <div className="font-semibold">{v.headline}</div>
                            <div className="text-sm">{v.primary_text}</div>
                            {v.description && <div className="text-xs text-muted-foreground">{v.description}</div>}
                            <Badge variant="secondary">CTA: {v.cta}</Badge>
                          </CardContent>
                        </Card>
                      ))}
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </CardContent>
          </Card>

          {/* Rationale */}
          <Collapsible>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer">
                  <CardTitle className="text-base">{t("assistant.rationale")} ▾</CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="text-sm space-y-2">
                  <div><span className="text-muted-foreground">Strategy:</span> {proposal.strategy_summary || proposal.rationale || "—"}</div>
                  <div><span className="text-muted-foreground">Funnel:</span> {(proposal.funnel_steps || []).join(" → ") || "—"}</div>
                  <div><span className="text-muted-foreground">Rules:</span> {(proposal.optimization_rules || []).join("; ") || "—"}</div>
                  {safety && (
                    <div>
                      <span className="text-muted-foreground">Safety:</span>{" "}
                      <Badge variant={safety.approved ? "default" : "destructive"}>
                        {safety.approved ? "approved" : "rejected"}
                      </Badge>{" "}
                      score {Number(safety.score || 0).toFixed(2)}
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Action bar */}
          <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t -mx-6 px-6 py-3 flex items-center gap-2 z-10"
               data-testid="assistant-action-bar">
            <Button variant="outline" onClick={() => setEditsOpen(true)} data-testid="assistant-edit">
              {t("assistant.edit")}
            </Button>
            <Button variant="ghost" onClick={() => setDiscardOpen(true)} data-testid="assistant-discard">
              {t("assistant.discard")}
            </Button>
            <Button onClick={materialize} disabled={materializing} className="ml-auto"
                    data-testid="assistant-create-campaign">
              {materializing ? t("assistant.materializing") : t("assistant.create_campaign")}
            </Button>
          </div>
        </div>
      )}

      {/* Edits dialog */}
      <Dialog open={editsOpen} onOpenChange={setEditsOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("assistant.edit")}</DialogTitle></DialogHeader>
          <Textarea rows={10} value={editsText} onChange={(e) => setEditsText(e.target.value)}
                    className="font-mono text-xs" data-testid="assistant-edits-text" />
          <p className="text-xs text-muted-foreground">{t("assistant.edits_help")}</p>
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost">{t("common.cancel")}</Button></DialogClose>
            <DialogClose asChild><Button data-testid="assistant-edits-apply">OK</Button></DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discard dialog */}
      <Dialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("assistant.discard_confirm")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("assistant.discard_help")}</p>
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost">{t("common.cancel")}</Button></DialogClose>
            <DialogClose asChild>
              <Button variant="destructive"
                      onClick={() => { setProposal(null); setRunId(null); setEditsText("{}"); }}
                      data-testid="assistant-discard-confirm">
                {t("assistant.discard")}
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
